import moment from 'moment';
import {
  fetchNewMarkAccessRecords as fetchNewMarkAccessRecordsService,
  addNewMarkRecord as addNewMarkRecordService,
} from '@intelligence/services/common';
import { NewMarkDisplayMode, NewMarkType } from '@intelligence/enums/common';
import { STATIC_NEW_MARK_CFG } from '@intelligence/_config/newMark';

import type { Action, Reducer, Effect } from 'umi';
import type { INewMarkAccessRecord, INewMarkCfg } from '@intelligence/types/newMark';

export type INewMarksState = Record<string, INewMarkCfg>;

type IUpdateNewMarksStateAction = Action<'updateNewMarksState'> & { payload: INewMarksState };

type IDeleteNewMarkStateAction = Action<'deleteNewMarkState'> & { payload: INewMarkCfg & { newMarkId: string } };

type IRevertDeleteNewMarkStateAction = Action<'revertDeleteNewMarkState'> & {
  payload: INewMarkCfg & { newMarkId: string };
};

interface INewMarksModel {
  namespace: string;
  state: INewMarksState;
  reducers: {
    updateNewMarksState: Reducer<INewMarksState, IUpdateNewMarksStateAction>;
    deleteNewMarkState: Reducer<INewMarksState, IDeleteNewMarkStateAction>;
    revertDeleteNewMarkState: Reducer<INewMarksState, IRevertDeleteNewMarkStateAction>;
  };
  effects: {
    fetchNewMarkRecords: Effect;
    addNewMarkRecord: Effect;
  };
}

export const NEW_MARKS_NAMESPACE = 'intelligence_new_marks';

/**
 * 合并左侧菜单、Tab页签、小红点的New标签、Beta标签配置
 * 其中左侧菜单的New标签、Beta标签配置 是根据左侧菜单接口返回的菜单数据中的tag字段生成的
 * Tab页签、小红点的New标签、Beta标签配置 由于无法配置在后端，所以是配置在前端的
 * @param menusNewMarkCfg
 * @return 左侧菜单、Tab页签、小红点 的 New标签、Beta标签的配置
 */
const mergeNewMarkCfg = (menusNewMarkCfg?: Record<string, INewMarkCfg> | null): Record<string, INewMarkCfg> => {
  return { ...STATIC_NEW_MARK_CFG, ...menusNewMarkCfg };
};

const getBusinessToday = (): moment.Moment => {
  return moment.utc().utcOffset(8 * 60);
};

const isExpiredNewMark = (cfg?: INewMarkCfg): boolean => {
  const { newMarkExpireDate } = cfg || {};
  if (!newMarkExpireDate) {
    return false;
  }

  const expireDate = moment(newMarkExpireDate, 'YYYY-MM-DD', true);
  if (!expireDate.isValid()) {
    return false;
  }

  return getBusinessToday().isSameOrAfter(expireDate, 'day');
};

/**
 * 根据newMarkCfg配置 以及 newMark访问记录 生成 需要展示的newMark配置
 * @param newMarkCfg newMark配置，包含左侧菜单、Tab页签、小红点 三种
 * @param newMarkAccessRecords newMark访问记录，表示用户点击过的newMark标签
 * @returns 用户还未点击过的newMark标签
 */
const generateNewMarksState = (
  newMarkCfg?: Record<string, INewMarkCfg> | null,
  newMarkAccessRecords?: INewMarkAccessRecord[] | null,
): INewMarksState => {
  const newMarkCfgTmp = newMarkCfg || {};
  const newMarkAccessRecordsTmp = newMarkAccessRecords || [];

  return Object.entries(newMarkCfgTmp).reduce<Record<string, INewMarkCfg>>((acc, cur) => {
    const [id, cfg] = cur;
    if (isExpiredNewMark(cfg)) return acc;
    if (cfg?.newMarkDisplayMode === NewMarkDisplayMode.Fixed) return { ...acc, [id]: cfg };
    // 是否访问过当前New标签、Beta标签
    const isAccessed = newMarkAccessRecordsTmp.find((item) => {
      const { new_mark_id: newMarkId, mark_type: markType } = item || {};
      return id === newMarkId && String(cfg?.newMarkType) === markType;
    });
    if (isAccessed) return acc;
    return { ...acc, [id]: cfg };
  }, {});
};

const newMarksModel: INewMarksModel = {
  namespace: NEW_MARKS_NAMESPACE,
  state: {},
  reducers: {
    updateNewMarksState(state, action) {
      if (!state) return {};
      if (!action?.payload) return { ...state };
      return { ...state, ...action.payload };
    },
    deleteNewMarkState(state, action) {
      if (!state) return {};
      const stateTmp: INewMarksState = {};
      const { newMarkType = NewMarkType.New, newMarkId } = action?.payload || {};
      Object.keys(state).forEach((key) => {
        const itemNewMarkCfg = state?.[key];
        if (key === newMarkId && itemNewMarkCfg?.newMarkType === newMarkType) return;
        stateTmp[key] = itemNewMarkCfg;
      });
      return stateTmp;
    },
    revertDeleteNewMarkState(state, action) {
      if (!state) return {};
      const {
        newMarkId,
        newMarkDisplayMode = NewMarkDisplayMode.RecordControlled,
        newMarkType = NewMarkType.New,
        newMarkExpireDate = '',
      } = action?.payload || {};
      if (!newMarkId) return { ...state };
      return { ...state, [newMarkId]: { newMarkDisplayMode, newMarkType, newMarkExpireDate } };
    },
  },
  effects: {
    *fetchNewMarkRecords({ payload }, { put, call }) {
      try {
        const { data: newMarkAccessRecords }: { data: any[] } = yield call(fetchNewMarkAccessRecordsService);
        const newMarkCfg = mergeNewMarkCfg(payload);
        const newMarksStateTmp = generateNewMarksState(newMarkCfg, newMarkAccessRecords);
        yield put({
          type: 'updateNewMarksState',
          payload: newMarksStateTmp || {},
        });
      } catch (exception) {
        yield put({
          type: 'updateNewMarksState',
          payload: {},
        });
      }
    },
    *addNewMarkRecord({ payload }, { put, call }) {
      try {
        /**
         * 固定展示的New、Beta标签不用访问记录影响，即使用户点击后，也不取消New、Beta标签的展示
         * 受访问记录控制是否展示的New、Beta标签 需要在用户点击后隐藏标签，并插入一条访问记录
         */
        if (payload?.newMarkDisplayMode === NewMarkDisplayMode.Fixed) return;
        yield put({
          type: 'deleteNewMarkState',
          payload,
        });
        yield call(addNewMarkRecordService, {
          new_mark_id: payload?.newMarkId,
          new_mark_type: payload?.newMarkType,
        });
      } catch (exception) {
        yield put({
          type: 'revertDeleteNewMarkState',
          payload,
        });
      }
    },
  },
};

export default newMarksModel;
